import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { blendApi, Recommendation } from "./api";
import OptimizerPanel, { OptimizeResult } from "./OptimizerPanel";
import { GENDER_AR, PerfRow } from "./ui";

function applyOptimization(base: Recommendation, opt: OptimizeResult): Recommendation {
  const boosters = base.boosters.map((b) => ({ ...b }));
  for (const add of opt.added) {
    const tokens = add.material.toLowerCase().split(/[/™]/).map((t) => t.trim()).filter(Boolean);
    const existing = boosters.find((b) => !b.isSignature && tokens.some((t) => b.material.toLowerCase().includes(t)));
    if (existing) existing.pct = Math.round((existing.pct + add.pct) * 100) / 100;
    else boosters.push({ material: add.material, pct: add.pct, isOptimizer: true } as any);
  }
  return {
    ...base,
    boosters,
    concentration: opt.newConcentration != null ? `${opt.newConcentration}%` : base.concentration,
    performance: {
      before: base.performance.before,
      after: opt.after,
      hours_before: base.performance.hours_before,
      hours_after: opt.hours_after,
    },
    warnings: [
      ...base.warnings,
      ...opt.newWarnings
        .filter((w) => !base.warnings.some((x) => x.text === w))
        .map((w) => ({ text: w, fix: "من ضبط الأداء — راجع قبل التصنيع" })),
    ],
  };
}

export default function RecommendationPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [base, setBase] = useState<Recommendation | null>(null);
  const [rec, setRec] = useState<Recommendation | null>(null);
  const [applied, setApplied] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    blendApi.recommendation(id)
      .then((r) => { setBase(r); setRec(r); setApplied(false); })
      .catch((e) => setErr(String(e.message || e)));
  }, [id]);

  if (err) return <div className="bl-sub py-8 text-center" style={{ color: "var(--bl-danger)" }} data-testid="rec-error">{err}</div>;
  if (!rec || !base) return <div className="bl-sub py-8 text-center" data-testid="rec-loading">جارٍ توليد التوصية…</div>;

  const { target: t, performance: p } = rec;

  const save = async () => {
    setSaving(true);
    try {
      await blendApi.saveBlend({
        base_target_id: t.id,
        name: `تركيبة ${t.name}${applied ? " (مضبوطة)" : ""}`,
        body: rec.body, direction: rec.direction,
        boosters: rec.boosters, concentration: rec.concentration,
        warnings: rec.warnings.map((w) => w.text),
      });
      toast.success("اتحفظت في تركيباتي");
    } catch (e: any) {
      toast.error(String(e.message || e));
    } finally { setSaving(false); }
  };

  const toCalculator = () => {
    sessionStorage.setItem("blend_handoff", JSON.stringify({
      name: `${t.name}${applied ? " (مضبوطة)" : ""}`, base_target_id: t.id,
      body: rec.body, direction: rec.direction,
      boosters: rec.boosters, concentration: rec.concentration,
      warnings: rec.warnings.map((w) => w.text),
    }));
    nav("/pureh/blend/calculator");
  };

  return (
    <div className="max-w-2xl" data-testid="recommendation-card">
      <div className="bl-card">
        <div className="bl-eyebrow">🎯 الهدف</div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="bl-title bl-en">{t.name}</span>
          <span className="bl-sub bl-en">{t.house} · {t.year}</span>
          <span className="bl-pill">{t.family_ar}</span>
          <span className="bl-pill">{GENDER_AR[t.gender]}</span>
          {applied && <span className="bl-pill" data-testid="rec-optimized-pill">مضبوطة ⚙️</span>}
        </div>
        <div className="bl-sub bl-en mt-2">DNA: {t.dna}</div>
        <div className="bl-sub bl-en" style={{ fontSize: 11 }}>Key chem: {t.key_chem}</div>
        <div className="bl-sub mt-1" data-testid="rec-signature-label">التوقيع: {t.signature}</div>

        <hr className="bl-divider" />
        <div className="bl-eyebrow mb-2">التركيبة المقترحة</div>
        <table className="bl-table" data-testid="rec-formula-table">
          <tbody>
            <tr><td>الأساس (Body)</td><td className="bl-en">{rec.body.oil}</td><td style={{ color: "var(--bl-gold)" }}>{rec.body.pct}%</td></tr>
            <tr><td>الاتجاه (Direction)</td><td className="bl-en">{rec.direction.oil}</td><td style={{ color: "var(--bl-gold)" }}>{rec.direction.pct}%</td></tr>
            {rec.boosters.map((b, i) => (
              <tr key={i}>
                <td>{b.isSignature ? "توقيع (accent)" : (b as any).isOptimizer ? "مُعزّز ⚙️" : "مُعزّز"}</td>
                <td className="bl-en">{b.material}</td>
                <td style={{ color: "var(--bl-gold)" }}>{b.pct}%</td>
              </tr>
            ))}
            <tr className="total"><td>التركيز</td><td colSpan={2} className="bl-en">{rec.concentration}</td></tr>
          </tbody>
        </table>

        <hr className="bl-divider" />
        <div className="bl-eyebrow mb-2">📈 الأداء المتوقّع</div>
        <div className="space-y-1" data-testid="rec-performance">
          <PerfRow label="الثبات" before={p.before.longevity} after={p.after.longevity} />
          <PerfRow label="النشر" before={p.before.projection} after={p.after.projection} />
          <PerfRow label="الأثر" before={p.before.sillage} after={p.after.sillage} />
          <div className="bl-sub bl-en">{p.hours_before} → {p.hours_after}</div>
        </div>

        <hr className="bl-divider" />
        <OptimizerPanel rec={base} applied={applied}
          onApply={(opt) => { setRec(applyOptimization(base, opt)); setApplied(true); toast.success("اتطبّق الضبط ✓"); }}
          onUndo={() => { setRec(base); setApplied(false); toast("رجعنا للتوصية الأساسية"); }} />

        {rec.warnings.length > 0 && (
          <>
            <hr className="bl-divider" />
            <div className="bl-eyebrow mb-2">⚠️ تحذيرات وحلولها</div>
            <div className="space-y-2" data-testid="rec-warnings">
              {rec.warnings.map((w, i) => (
                <div key={i} className="text-sm">
                  <span className="bl-pill danger">{w.text}</span>
                  <span className="bl-sub mr-2">← {w.fix}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <hr className="bl-divider" />
        <div className="flex gap-2 flex-wrap">
          <button className="bl-btn ghost" onClick={save} disabled={saving} data-testid="rec-save-btn">
            💾 {saving ? "بيحفظ…" : "احفظ"}
          </button>
          <button className="bl-btn primary" onClick={toCalculator} data-testid="rec-to-calculator-btn">
            ▶️ حوّل لوصفة (الحاسبة)
          </button>
          <button
            className="bl-btn ghost"
            data-testid="rec-ai-assist-btn"
            onClick={() => window.dispatchEvent(new CustomEvent("orvan-ai-open", {
              detail: { mode: "rnd", context: {
                target: t.name, family: t.family, signature: t.signature,
                recipe: { body: rec.body, direction: rec.direction, boosters: rec.boosters, concentration: rec.concentration },
                performance: p, warnings: rec.warnings.map((w) => w.text),
              } },
            }))}
          >
            ✨ اسأل مساعد R&D
          </button>
        </div>
        <p className="bl-disclaimer mt-3" data-testid="rec-disclaimer">* {rec.disclaimer}</p>
      </div>
    </div>
  );
}
